import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { 
  AlertTriangle, Check, Loader2, Package, Search, Barcode,
  ClipboardCheck, Plus, Minus, RefreshCw, History, Play, 
  StopCircle, Trash2, ArrowDown, ArrowUp, Filter, Truck, Tag, Printer
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, formatDateTime } from '../utils/dateFormat';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StockCount = () => {
  const { getAuthHeader } = useAuth();
  const { stores, storeName } = useStore();
  const [countStore, setCountStore] = useState('orewa');
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('stocktake');
  
  // Stock Take Session State
  const [activeStockTake, setActiveStockTake] = useState(null);
  const [stockTakeItems, setStockTakeItems] = useState([]);
  const [stockTakeHistory, setStockTakeHistory] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showStartDialog, setShowStartDialog] = useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [filterValue, setFilterValue] = useState('');
  const [filterName, setFilterName] = useState('');
  const [starting, setStarting] = useState(false);
  const [completing, setCompleting] = useState(false);
  // Product IDs whose auto-save FAILED — shown in a persistent banner and
  // re-pushed before completion. Counts must never be silently lost again.
  const [failedSaves, setFailedSaves] = useState([]);

  // History viewer/editor state
  const [viewingStockTake, setViewingStockTake] = useState(null);  // full stock-take doc
  const [viewerLoading, setViewerLoading] = useState(false);
  const [viewerSearch, setViewerSearch] = useState('');
  const [editingRowProductId, setEditingRowProductId] = useState(null);
  const [editingRowValue, setEditingRowValue] = useState('');
  const [savingRow, setSavingRow] = useState(false);

  const openStockTakeViewer = async (st) => {
    setViewerLoading(true);
    setViewingStockTake({ ...st, items: [] });
    setViewerSearch('');
    setEditingRowProductId(null);
    try {
      const res = await axios.get(`${API_URL}/stock-takes/${st.id}`, { headers: getAuthHeader() });
      setViewingStockTake(res.data);
    } catch (err) {
      toast.error('Failed to load stock take details');
      setViewingStockTake(null);
    } finally {
      setViewerLoading(false);
    }
  };

  const saveEditedCount = async () => {
    if (!viewingStockTake || !editingRowProductId) return;
    const newCounted = parseInt(editingRowValue, 10);
    if (Number.isNaN(newCounted) || newCounted < 0) {
      toast.error('Enter a valid non-negative number');
      return;
    }
    setSavingRow(true);
    try {
      const res = await axios.patch(
        `${API_URL}/stock-takes/${viewingStockTake.id}/items/${editingRowProductId}`,
        { counted: newCounted },
        { headers: getAuthHeader() }
      );
      // Refresh the viewer doc
      const full = await axios.get(`${API_URL}/stock-takes/${viewingStockTake.id}`, { headers: getAuthHeader() });
      setViewingStockTake(full.data);
      // Refresh history list (totals may have changed)
      const histRes = await axios.get(`${API_URL}/stock-takes`, { headers: getAuthHeader() });
      setStockTakeHistory(histRes.data);
      setEditingRowProductId(null);
      setEditingRowValue('');
      const applied = res.data?.applied_to_live_stock;
      toast.success(applied
        ? `Updated. Live stock adjusted by ${res.data.delta > 0 ? '+' : ''}${res.data.delta}.`
        : 'Stock take item updated.');
    } catch (err) {
      toast.error('Failed to save: ' + (err?.response?.data?.detail || err.message));
    } finally {
      setSavingRow(false);
    }
  };
  
  // Scanner reference
  const searchInputRef = useRef(null);
  const lastScanTime = useRef(0);

  // Fetch data
  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/products?limit=15000`, { headers: getAuthHeader() });
      setProducts(response.data);
      
      // Extract unique brands
      const uniqueBrands = [...new Set(response.data.map(p => p.brand).filter(Boolean))];
      setBrands(uniqueBrands.sort());
    } catch (error) {
      toast.error('Failed to load products');
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API_URL}/suppliers`, { headers: getAuthHeader() });
      setSuppliers(response.data);
    } catch (error) {
      console.log('Failed to load suppliers');
    }
  };

  const fetchStockTakeHistory = async () => {
    try {
      const response = await axios.get(`${API_URL}/stock-takes`, { headers: getAuthHeader() });
      setStockTakeHistory(response.data);

      // Check for in-progress stock take. The list endpoint no longer returns
      // the (potentially huge) embedded items array — fetch it separately.
      const inProgress = response.data.find(st => st.status === 'in_progress');
      if (inProgress) {
        try {
          const full = await axios.get(
            `${API_URL}/stock-takes/${inProgress.id}`,
            { headers: getAuthHeader(), timeout: 30000 }
          );
          setActiveStockTake(full.data);
          setStockTakeItems(full.data.items || []);
          setCountStore(full.data.store);
        } catch (innerErr) {
          // If the in-progress record is too big to load (rare), don't block
          // the rest of the page — surface a toast but keep loading=false.
          console.error('Failed to load in-progress stock take items:', innerErr);
          toast.error('Couldn\'t load the active stock take items. You can cancel it from History.');
          setActiveStockTake({ ...inProgress, items: [] });
          setStockTakeItems([]);
          setCountStore(inProgress.store);
        }
      }
    } catch (error) {
      console.log('Failed to load stock take history', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchSuppliers();
    fetchStockTakeHistory();
  }, []);

  // Get stock for selected store
  const getStoreStock = useCallback((product) => {
    if (countStore === 'orewa') return product.stock_orewa || 0;
    if (countStore === 'eastridge') return product.stock_eastridge || 0;
    return product.quantity || 0;
  }, [countStore]);

  // Filter products based on current filter settings
  const getFilteredProducts = useCallback(() => {
    if (!activeStockTake) return [];
    
    let filtered = products;
    
    if (activeStockTake.filter_type === 'supplier' && activeStockTake.filter_value) {
      filtered = products.filter(p => 
        p.supplier_id === activeStockTake.filter_value || 
        p.supplier_name === activeStockTake.filter_name
      );
    } else if (activeStockTake.filter_type === 'brand' && activeStockTake.filter_value) {
      filtered = products.filter(p => p.brand === activeStockTake.filter_value);
    }
    
    return filtered;
  }, [products, activeStockTake]);

  // Search for product by barcode or name
  const handleSearch = useCallback((term) => {
    if (!term || !activeStockTake) return;
    
    const filteredProducts = getFilteredProducts();
    const found = filteredProducts.find(p => 
      p.barcode === term || 
      p.sku === term ||
      p.name.toLowerCase() === term.toLowerCase()
    );
    
    if (found) {
      // Check if already in list
      const existingIndex = stockTakeItems.findIndex(item => item.product_id === found.id);
      
      if (existingIndex >= 0) {
        // Increment counted
        const newItems = [...stockTakeItems];
        newItems[existingIndex] = {
          ...newItems[existingIndex],
          counted: newItems[existingIndex].counted + 1,
          variance: (newItems[existingIndex].counted + 1) - newItems[existingIndex].system_stock
        };
        setStockTakeItems(newItems);
        toast.success(`${found.name}: ${newItems[existingIndex].counted} counted`);
      } else {
        // Add new item
        const systemStock = getStoreStock(found);
        const newItem = {
          product_id: found.id,
          product_name: found.name,
          sku: found.sku || '',
          barcode: found.barcode || '',
          buy_price: found.buy_price || 0,
          system_stock: systemStock,
          counted: 1,
          variance: 1 - systemStock
        };
        setStockTakeItems(prev => [newItem, ...prev]);
        toast.success(`Added: ${found.name}`);
      }
      
      setSearchTerm('');
      
      // Auto-save just this line to server (single-item save — reliable on huge counts)
      if (existingIndex >= 0) {
        saveSingleItem({
          ...stockTakeItems[existingIndex],
          counted: stockTakeItems[existingIndex].counted + 1,
          variance: (stockTakeItems[existingIndex].counted + 1) - stockTakeItems[existingIndex].system_stock
        });
      } else {
        saveSingleItem({
          product_id: found.id,
          product_name: found.name,
          sku: found.sku || '',
          barcode: found.barcode || '',
          buy_price: found.buy_price || 0,
          system_stock: getStoreStock(found),
          counted: 1,
          variance: 1 - getStoreStock(found)
        });
      }
    } else {
      // Try partial name search
      const matches = filteredProducts.filter(p =>
        p.name.toLowerCase().includes(term.toLowerCase()) ||
        p.sku?.toLowerCase().includes(term.toLowerCase())
      );
      
      if (matches.length === 1) {
        // Auto-select single match
        handleSearch(matches[0].barcode || matches[0].name);
      } else if (matches.length > 1) {
        toast.info(`${matches.length} products found. Select from list below.`);
      } else {
        toast.error('Product not found in current filter');
      }
    }
  }, [activeStockTake, stockTakeItems, getFilteredProducts, getStoreStock]);

  // Handle barcode scanner input
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && searchTerm) {
      e.preventDefault();
      handleSearch(searchTerm);
    }
  }, [searchTerm, handleSearch]);

  // Save a SINGLE item line to the server. The old approach PUT the entire items
  // array on every count entry — on big stocktakes (10k+ pre-populated lines) the
  // payload either failed validation (null counted) or exceeded request limits,
  // so counts silently never reached the server and completed stocktakes showed
  // zero variance. Single-line saves are tiny and reliable.
  const saveSingleItem = async (item) => {
    if (!activeStockTake) return;

    try {
      await axios.put(
        `${API_URL}/stock-takes/${activeStockTake.id}/item`,
        {
          product_id: item.product_id,
          product_name: item.product_name,
          sku: item.sku || '',
          barcode: item.barcode || '',
          buy_price: item.buy_price || 0,
          system_stock: item.system_stock,
          counted: item.counted,
          variance: item.variance
        },
        { headers: getAuthHeader() }
      );
      setFailedSaves(prev => prev.filter(id => id !== item.product_id));
    } catch (error) {
      console.error('Failed to save stock take item', error);
      setFailedSaves(prev => prev.includes(item.product_id) ? prev : [...prev, item.product_id]);
      toast.error(`Count NOT saved for ${item.product_name} — check connection and re-enter it.`, { duration: 8000 });
    }
  };

  // Retry all failed saves in one batch (merge endpoint — never removes lines)
  const retryFailedSaves = async () => {
    if (!activeStockTake || failedSaves.length === 0) return;
    const itemsToSync = stockTakeItems.filter(i => failedSaves.includes(i.product_id));
    try {
      await axios.post(
        `${API_URL}/stock-takes/${activeStockTake.id}/sync-counted`,
        itemsToSync.map(item => ({
          product_id: item.product_id,
          product_name: item.product_name,
          sku: item.sku || '',
          barcode: item.barcode || '',
          buy_price: item.buy_price || 0,
          system_stock: item.system_stock,
          counted: item.counted,
          variance: item.variance
        })),
        { headers: getAuthHeader() }
      );
      setFailedSaves([]);
      toast.success(`${itemsToSync.length} count${itemsToSync.length > 1 ? 's' : ''} saved successfully.`);
    } catch (error) {
      toast.error('Still failing to save — check your internet connection and try again.');
    }
  };

  // Update item count
  const updateItemCount = (productId, newCount) => {
    let changedItem = null;
    const newItems = stockTakeItems.map(item => {
      if (item.product_id === productId) {
        changedItem = {
          ...item,
          counted: Math.max(0, newCount),
          variance: Math.max(0, newCount) - item.system_stock
        };
        return changedItem;
      }
      return item;
    });
    setStockTakeItems(newItems);
    if (changedItem) saveSingleItem(changedItem);
  };

  // Remove item from count
  const removeItem = async (productId) => {
    const newItems = stockTakeItems.filter(item => item.product_id !== productId);
    setStockTakeItems(newItems);
    try {
      await axios.delete(
        `${API_URL}/stock-takes/${activeStockTake.id}/item/${productId}`,
        { headers: getAuthHeader() }
      );
      toast.success('Item removed from count');
    } catch (error) {
      toast.error('Failed to remove item — refresh and try again.');
    }
  };

  // Refresh a single line's "system stock" baseline from live inventory.
  // Useful when stock moved (transfer / receive / sale) AFTER the stock take
  // started — re-syncs the variance so it doesn't show a false loss/gain.
  const refreshSystemStock = async (productId) => {
    if (!activeStockTake) return;
    try {
      await axios.post(
        `${API_URL}/stock-takes/${activeStockTake.id}/refresh-system-stock?product_id=${productId}`,
        {},
        { headers: getAuthHeader() }
      );
      const full = await axios.get(
        `${API_URL}/stock-takes/${activeStockTake.id}`,
        { headers: getAuthHeader() }
      );
      setActiveStockTake(full.data);
      setStockTakeItems(full.data.items || []);
      toast.success('Baseline refreshed from live stock');
    } catch (err) {
      toast.error('Failed to refresh from live stock');
    }
  };

  // Start new stock take — items are pre-populated SERVER-side (the old
  // client-side pre-populate uploaded the entire product list, which failed
  // silently on big stocktakes and caused "no variance" completions).
  const handleStartStockTake = async () => {
    setStarting(true);
    try {
      const response = await axios.post(`${API_URL}/stock-takes`, {
        store: countStore,
        filter_type: filterType,
        filter_value: filterValue,
        filter_name: filterName
      }, { headers: getAuthHeader() });
      
      setActiveStockTake(response.data);
      setStockTakeItems(response.data.items || []);
      setShowStartDialog(false);
      
      toast.success(`Stock take started with ${(response.data.items || []).length} products! Enter counted quantities.`);
      
      // Focus search input
      setTimeout(() => searchInputRef.current?.focus(), 100);
    } catch (error) {
      toast.error('Failed to start stock take');
    } finally {
      setStarting(false);
    }
  };

  // Print count sheet for physical counting
  const handlePrintCountSheet = () => {
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    const filterLabel = activeStockTake?.filter_type === 'all' ? 'All Products' :
      activeStockTake?.filter_type === 'supplier' ? `Supplier: ${activeStockTake?.filter_name}` :
      `Brand: ${activeStockTake?.filter_value}`;

    // Sort alphabetically for shelf-walk efficiency
    const sortedItems = [...stockTakeItems].sort((a, b) =>
      (a.product_name || '').localeCompare(b.product_name || '')
    );

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Stock Count Sheet - ${filterLabel}</title>
        <style>
          @page { size: A4 portrait; margin: 8mm 8mm 10mm 8mm; }
          * { box-sizing: border-box; }
          body { font-family: Arial, Helvetica, sans-serif; padding: 0; font-size: 9px; color: #111; }
          h1 { font-size: 13px; margin: 0 0 2px 0; letter-spacing: 0.5px; }
          .info { margin-bottom: 6px; color: #555; font-size: 9px; }
          table { width: 100%; border-collapse: collapse; table-layout: fixed; }
          th, td { border: 0.5px solid #999; padding: 2px 4px; text-align: left; vertical-align: middle; line-height: 1.15; }
          th { background: #1e293b; color: #fff; font-weight: 700; font-size: 9px; padding: 3px 4px; }
          .num { text-align: center; font-weight: 700; }
          .barcode { font-family: 'Courier New', monospace; font-size: 8.5px; color: #444; word-break: break-all; }
          .count-cell { height: 14px; }
          tr { page-break-inside: avoid; }
          tr:nth-child(even) td { background: #f5f7fa; }
          thead { display: table-header-group; }   /* repeat header on every page */
          tfoot { display: table-footer-group; }
          .footer-note { margin-top: 6px; font-size: 8.5px; color: #555; }
          @media print { body { padding: 0; } .no-print { display: none; } }
        </style>
      </head>
      <body>
        <h1>STOCK COUNT SHEET</h1>
        <div class="info">
          <strong>Store:</strong> ${activeStockTake?.store?.charAt(0).toUpperCase() + activeStockTake?.store?.slice(1)} &nbsp;|&nbsp;
          <strong>${filterLabel}</strong> &nbsp;|&nbsp;
          <strong>Date:</strong> ${new Date().toLocaleDateString('en-NZ')} &nbsp;|&nbsp;
          <strong>Products:</strong> ${sortedItems.length}
        </div>
        <table>
          <colgroup>
            <col style="width: 4%"  />
            <col style="width: 48%" />
            <col style="width: 18%" />
            <col style="width: 10%" />
            <col style="width: 12%" />
            <col style="width: 8%"  />
          </colgroup>
          <thead>
            <tr>
              <th>#</th>
              <th>Product Name</th>
              <th>Barcode</th>
              <th class="num">System</th>
              <th class="num">Counted</th>
              <th class="num">Var.</th>
            </tr>
          </thead>
          <tbody>
            ${sortedItems.map((item, idx) => `
              <tr>
                <td class="num">${idx + 1}</td>
                <td>${(item.product_name || '').replace(/</g, '&lt;')}</td>
                <td class="barcode">${item.barcode || item.sku || ''}</td>
                <td class="num">${item.system_stock}</td>
                <td class="count-cell"></td>
                <td></td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        <div class="footer-note">
          Counted by: _____________________ &nbsp; Signature: _____________________ &nbsp; Date: _____ / _____ / _____
        </div>
        <script>
          window.onload = function() { window.print(); }
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
    toast.success('Print dialog opened');
  };

  // Complete stock take — with a PRE-COMPLETE SYNC CHECK.
  // Before completing we verify every locally-counted line actually exists on
  // the server with the same counted value. Any missing/mismatched counts are
  // re-pushed first; if they still can't be saved, completion is BLOCKED.
  // This makes it impossible to complete a stocktake whose counts were lost.
  const handleCompleteStockTake = async (applyAdjustments = true) => {
    if (!activeStockTake) return;
    
    setCompleting(true);
    try {
      // 1) Fetch what the server actually has
      const verifyRes = await axios.get(`${API_URL}/stock-takes/${activeStockTake.id}`, { headers: getAuthHeader() });
      const serverCounts = {};
      (verifyRes.data.items || []).forEach(i => { if (i.counted !== null && i.counted !== undefined) serverCounts[i.product_id] = i.counted; });
      
      // 2) Find locally-counted lines missing or different on the server
      const localCounted = stockTakeItems.filter(i => i.counted !== null);
      const outOfSync = localCounted.filter(i => serverCounts[i.product_id] !== i.counted);
      
      // 3) Re-push them (chunked) before completing
      if (outOfSync.length > 0) {
        toast.info(`Syncing ${outOfSync.length} unsaved count${outOfSync.length > 1 ? 's' : ''} before completing...`);
        for (let c = 0; c < outOfSync.length; c += 500) {
          const chunk = outOfSync.slice(c, c + 500);
          await axios.post(
            `${API_URL}/stock-takes/${activeStockTake.id}/sync-counted`,
            chunk.map(item => ({
              product_id: item.product_id,
              product_name: item.product_name,
              sku: item.sku || '',
              barcode: item.barcode || '',
              buy_price: item.buy_price || 0,
              system_stock: item.system_stock,
              counted: item.counted,
              variance: item.variance
            })),
            { headers: getAuthHeader() }
          );
        }
        // Re-verify after sync
        const recheck = await axios.get(`${API_URL}/stock-takes/${activeStockTake.id}`, { headers: getAuthHeader() });
        const recheckCounts = {};
        (recheck.data.items || []).forEach(i => { if (i.counted !== null && i.counted !== undefined) recheckCounts[i.product_id] = i.counted; });
        const stillMissing = localCounted.filter(i => recheckCounts[i.product_id] !== i.counted);
        if (stillMissing.length > 0) {
          toast.error(`${stillMissing.length} count(s) could NOT be saved to the server. Completion blocked — nothing has been lost on your screen. Check your connection and try again.`, { duration: 12000 });
          setCompleting(false);
          return;
        }
        setFailedSaves([]);
      }
      
      const response = await axios.post(
        `${API_URL}/stock-takes/${activeStockTake.id}/complete?apply_adjustments=${applyAdjustments}`,
        {},
        { headers: getAuthHeader() }
      );
      
      toast.success(`Stock take completed! Loss: $${response.data.total_loss_value}, Gain: $${response.data.total_gain_value}`);
      setActiveStockTake(null);
      setStockTakeItems([]);
      setFailedSaves([]);
      setShowCompleteDialog(false);
      fetchStockTakeHistory();
      fetchProducts(); // Refresh stock levels
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete stock take');
    } finally {
      setCompleting(false);
    }
  };

  // Cancel stock take
  const handleCancelStockTake = async () => {
    if (!activeStockTake) return;
    
    if (!window.confirm('Are you sure you want to cancel this stock take? All counted items will be lost.')) {
      return;
    }
    
    try {
      await axios.delete(`${API_URL}/stock-takes/${activeStockTake.id}`, { headers: getAuthHeader() });
      setActiveStockTake(null);
      setStockTakeItems([]);
      toast.success('Stock take cancelled');
      fetchStockTakeHistory();
    } catch (error) {
      toast.error('Failed to cancel stock take');
    }
  };

  // Calculate totals - only for items that have been counted (counted !== null)
  const countedItems = stockTakeItems.filter(i => i.counted !== null);
  const totalVariance = countedItems.reduce((sum, item) => sum + item.variance, 0);
  const totalLossUnits = countedItems.filter(i => i.variance < 0).reduce((sum, i) => sum + Math.abs(i.variance), 0);
  const totalGainUnits = countedItems.filter(i => i.variance > 0).reduce((sum, i) => sum + i.variance, 0);
  const totalLossValue = countedItems.filter(i => i.variance < 0).reduce((sum, i) => sum + (Math.abs(i.variance) * (i.buy_price || 0)), 0);
  const totalGainValue = countedItems.filter(i => i.variance > 0).reduce((sum, i) => sum + (i.variance * (i.buy_price || 0)), 0);
  const itemsCountedCount = countedItems.length;

  // Add product from search results
  const addProductToCount = (product) => {
    const existingIndex = stockTakeItems.findIndex(item => item.product_id === product.id);
    
    if (existingIndex >= 0) {
      updateItemCount(product.id, stockTakeItems[existingIndex].counted + 1);
    } else {
      const systemStock = getStoreStock(product);
      const newItem = {
        product_id: product.id,
        product_name: product.name,
        sku: product.sku || '',
        barcode: product.barcode || '',
        buy_price: product.buy_price || 0,
        system_stock: systemStock,
        counted: 0,
        variance: 0 - systemStock
      };
      const newItems = [newItem, ...stockTakeItems];
      setStockTakeItems(newItems);
      saveSingleItem(newItem);
    }
    setSearchTerm('');
  };

  // Search results for dropdown
  const searchResults = searchTerm.length >= 2 ? getFilteredProducts().filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.barcode?.includes(searchTerm)
  ).slice(0, 10) : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="stock-count-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-slate-900">
            Stock Take
          </h1>
          <p className="text-slate-500 mt-1">Count inventory by supplier, brand, or all products</p>
        </div>
        <div className="flex items-center gap-3">
          {!activeStockTake && (
            <Button 
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={() => setShowStartDialog(true)}
              data-testid="start-stocktake-btn"
            >
              <Play className="w-4 h-4 mr-2" /> Start Stock Take
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="stocktake" className="flex items-center gap-2">
            <ClipboardCheck className="w-4 h-4" />
            Stock Take {activeStockTake && <Badge className="bg-emerald-500 ml-1">Active</Badge>}
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <History className="w-4 h-4" />
            History ({stockTakeHistory.filter(st => st.status === 'completed').length})
          </TabsTrigger>
        </TabsList>

        {/* STOCK TAKE TAB */}
        <TabsContent value="stocktake" className="mt-4">
          {!activeStockTake ? (
            <Card className="border-2 border-dashed border-slate-300">
              <CardContent className="py-16 text-center">
                <ClipboardCheck className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <h3 className="text-xl font-bold text-slate-700 mb-2">No Active Stock Take</h3>
                <p className="text-slate-500 mb-6">Start a new stock take to count inventory by supplier, brand, or all products.</p>
                <Button 
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => setShowStartDialog(true)}
                >
                  <Play className="w-4 h-4 mr-2" /> Start Stock Take
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {/* Active Stock Take Info Bar */}
              <Card className="bg-emerald-50 border-emerald-200">
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <Badge className="bg-emerald-600 text-white px-3 py-1">
                        <Play className="w-3 h-3 mr-1" /> Active
                      </Badge>
                      <div>
                        <p className="font-medium text-emerald-800">
                          {activeStockTake.filter_type === 'all' ? 'All Products' :
                           activeStockTake.filter_type === 'supplier' ? `Supplier: ${activeStockTake.filter_name}` :
                           `Brand: ${activeStockTake.filter_value}`}
                        </p>
                        <p className="text-sm text-emerald-600">
                          Store: {activeStockTake.store.charAt(0).toUpperCase() + activeStockTake.store.slice(1)} • 
                          Started: {formatDateTime(activeStockTake.created_at)} •
                          {stockTakeItems.length} products
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline"
                        onClick={handlePrintCountSheet}
                        disabled={stockTakeItems.length === 0}
                      >
                        <Printer className="w-4 h-4 mr-1" /> Print Count Sheet
                      </Button>
                      <Button 
                        variant="outline" 
                        className="border-red-300 text-red-600 hover:bg-red-50"
                        onClick={handleCancelStockTake}
                      >
                        <Trash2 className="w-4 h-4 mr-1" /> Cancel
                      </Button>
                      <Button 
                        className="bg-blue-600 hover:bg-blue-700"
                        onClick={() => setShowCompleteDialog(true)}
                        disabled={stockTakeItems.length === 0 || failedSaves.length > 0}
                        title={failedSaves.length > 0 ? 'Save all counts before completing' : undefined}
                      >
                        <StopCircle className="w-4 h-4 mr-1" /> Complete
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* UNSAVED COUNTS WARNING — shown whenever any auto-save failed */}
              {failedSaves.length > 0 && (
                <Card className="bg-red-50 border-2 border-red-400" data-testid="unsaved-counts-banner">
                  <CardContent className="py-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <AlertTriangle className="w-6 h-6 text-red-600 shrink-0" />
                        <div>
                          <p className="font-bold text-red-800">
                            {failedSaves.length} count{failedSaves.length > 1 ? 's have' : ' has'} NOT been saved to the server
                          </p>
                          <p className="text-sm text-red-700">Your counts are safe on this screen — click Retry to save them. Completion is blocked until everything is saved.</p>
                        </div>
                      </div>
                      <Button className="bg-red-600 hover:bg-red-700 text-white shrink-0" onClick={retryFailedSaves} data-testid="retry-failed-saves-btn">
                        Retry now
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Search / Scanner Input */}
              <Card>
                <CardContent className="py-4">
                  <div className="flex items-center gap-4">
                    <div className="relative flex-1">
                      <Barcode className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        ref={searchInputRef}
                        placeholder="Scan barcode or search product name..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={handleKeyDown}
                        className="pl-12 h-12 text-lg"
                        autoFocus
                        data-testid="stocktake-search"
                      />
                      {/* Search Results Dropdown */}
                      {searchResults.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-64 overflow-auto">
                          {searchResults.map(product => (
                            <button
                              key={product.id}
                              className="w-full px-4 py-3 text-left hover:bg-slate-50 border-b last:border-b-0 flex items-center justify-between"
                              onClick={() => addProductToCount(product)}
                            >
                              <div>
                                <p className="font-medium">{product.name}</p>
                                <p className="text-sm text-slate-500">
                                  {product.sku && `SKU: ${product.sku} • `}
                                  {product.barcode && `Barcode: ${product.barcode}`}
                                </p>
                              </div>
                              <Badge variant="outline">Stock: {getStoreStock(product)}</Badge>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <Button variant="outline" onClick={() => searchInputRef.current?.focus()}>
                      <Search className="w-4 h-4 mr-2" /> Focus
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <Card className="bg-slate-50">
                  <CardContent className="py-4">
                    <p className="text-sm text-slate-600">Total Products</p>
                    <p className="text-3xl font-bold text-slate-800">{stockTakeItems.length}</p>
                  </CardContent>
                </Card>
                <Card className="bg-emerald-50">
                  <CardContent className="py-4">
                    <p className="text-sm text-slate-600">Counted</p>
                    <p className="text-3xl font-bold text-emerald-600">{itemsCountedCount}</p>
                    <p className="text-xs text-emerald-500">{stockTakeItems.length > 0 ? Math.round((itemsCountedCount / stockTakeItems.length) * 100) : 0}% done</p>
                  </CardContent>
                </Card>
                <Card className={`${totalVariance === 0 ? 'bg-slate-50' : totalVariance < 0 ? 'bg-red-50' : 'bg-blue-50'}`}>
                  <CardContent className="py-4">
                    <p className="text-sm text-slate-600">Total Variance</p>
                    <p className={`text-3xl font-bold ${totalVariance === 0 ? 'text-slate-600' : totalVariance < 0 ? 'text-red-600' : 'text-blue-600'}`}>
                      {totalVariance > 0 ? '+' : ''}{totalVariance}
                    </p>
                  </CardContent>
                </Card>
                <Card className="bg-red-50">
                  <CardContent className="py-4">
                    <p className="text-sm text-slate-600 flex items-center gap-1">
                      <ArrowDown className="w-4 h-4" /> Loss
                    </p>
                    <p className="text-2xl font-bold text-red-600">{totalLossUnits} units</p>
                    <p className="text-sm text-red-500">${totalLossValue.toFixed(2)}</p>
                  </CardContent>
                </Card>
                <Card className="bg-blue-50">
                  <CardContent className="py-4">
                    <p className="text-sm text-slate-600 flex items-center gap-1">
                      <ArrowUp className="w-4 h-4" /> Gain
                    </p>
                    <p className="text-2xl font-bold text-blue-600">{totalGainUnits} units</p>
                    <p className="text-sm text-blue-500">${totalGainValue.toFixed(2)}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Counted Items Table */}
              <Card>
                <CardHeader className="border-b">
                  <CardTitle className="flex items-center gap-2">
                    <Package className="w-5 h-5" />
                    Products to Count ({stockTakeItems.length})
                    {itemsCountedCount > 0 && (
                      <Badge className="bg-emerald-100 text-emerald-700 ml-2">
                        {itemsCountedCount} counted
                      </Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <ScrollArea className="h-[400px]">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50 sticky top-0">
                          <TableHead className="font-heading font-bold uppercase text-xs">Product</TableHead>
                          <TableHead className="font-heading font-bold uppercase text-xs text-center">System</TableHead>
                          <TableHead className="font-heading font-bold uppercase text-xs text-center">Counted</TableHead>
                          <TableHead className="font-heading font-bold uppercase text-xs text-center">Variance</TableHead>
                          <TableHead className="font-heading font-bold uppercase text-xs text-right">Value</TableHead>
                          <TableHead className="font-heading font-bold uppercase text-xs w-12"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {stockTakeItems.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} className="text-center py-12 text-slate-500">
                              <Barcode className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                              <p>Select a supplier or brand to load products for counting</p>
                            </TableCell>
                          </TableRow>
                        ) : (
                          stockTakeItems.map((item) => (
                            <TableRow 
                              key={item.product_id} 
                              className={
                                item.counted === null ? 'bg-slate-50' :
                                item.variance < 0 ? 'bg-red-50' : 
                                item.variance > 0 ? 'bg-blue-50' : 'bg-emerald-50'
                              }
                            >
                              <TableCell>
                                <p className="font-medium">{item.product_name}</p>
                                <p className="text-xs text-slate-500">
                                  {item.sku && `SKU: ${item.sku}`}
                                  {item.sku && item.barcode && ' • '}
                                  {item.barcode && item.barcode}
                                </p>
                              </TableCell>
                              <TableCell className="text-center">
                                <Badge variant="outline">{item.system_stock}</Badge>
                              </TableCell>
                              <TableCell className="text-center">
                                <div className="flex items-center justify-center gap-1">
                                  <Button
                                    size="icon"
                                    variant="outline"
                                    className="h-7 w-7"
                                    onClick={() => updateItemCount(item.product_id, (item.counted || 0) - 1)}
                                  >
                                    <Minus className="w-3 h-3" />
                                  </Button>
                                  <Input
                                    type="number"
                                    min="0"
                                    value={item.counted === null ? '' : item.counted}
                                    placeholder="—"
                                    onChange={(e) => updateItemCount(item.product_id, e.target.value === '' ? 0 : parseInt(e.target.value) || 0)}
                                    className={`w-16 h-7 text-center font-bold ${item.counted === null ? 'bg-yellow-50 border-yellow-300' : ''}`}
                                  />
                                  <Button
                                    size="icon"
                                    variant="outline"
                                    className="h-7 w-7"
                                    onClick={() => updateItemCount(item.product_id, (item.counted || 0) + 1)}
                                  >
                                    <Plus className="w-3 h-3" />
                                  </Button>
                                </div>
                              </TableCell>
                              <TableCell className="text-center">
                                {item.counted === null ? (
                                  <Badge variant="outline" className="bg-yellow-50 text-yellow-600 border-yellow-300">
                                    Not counted
                                  </Badge>
                                ) : (
                                  <Badge 
                                    className={
                                      item.variance < 0 ? 'bg-red-100 text-red-700' :
                                      item.variance > 0 ? 'bg-blue-100 text-blue-700' :
                                      'bg-emerald-100 text-emerald-700'
                                    }
                                  >
                                    {item.variance > 0 ? '+' : ''}{item.variance}
                                  </Badge>
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                {item.counted !== null && item.variance !== 0 && (
                                  <span className={item.variance < 0 ? 'text-red-600' : 'text-blue-600'}>
                                    {item.variance < 0 ? '-' : '+'}${Math.abs(item.variance * (item.buy_price || 0)).toFixed(2)}
                                  </span>
                                )}
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1">
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-7 w-7 text-blue-600 hover:bg-blue-50"
                                    onClick={() => refreshSystemStock(item.product_id)}
                                    title="Refresh system stock from live inventory (use after a transfer/receive/sale happened mid-count)"
                                    data-testid={`refresh-system-stock-${item.product_id}`}
                                  >
                                    <RefreshCw className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-7 w-7 text-red-500 hover:bg-red-50"
                                    onClick={() => removeItem(item.product_id)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* HISTORY TAB */}
        <TabsContent value="history" className="mt-4">
          <Card>
            <CardHeader className="border-b">
              <CardTitle className="flex items-center gap-2">
                <History className="w-5 h-5" />
                Stock Take History
              </CardTitle>
              <CardDescription>View completed stock takes and their results</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-heading font-bold uppercase text-xs">Date</TableHead>
                    <TableHead className="font-heading font-bold uppercase text-xs">Store</TableHead>
                    <TableHead className="font-heading font-bold uppercase text-xs">Filter</TableHead>
                    <TableHead className="font-heading font-bold uppercase text-xs text-center">Items</TableHead>
                    <TableHead className="font-heading font-bold uppercase text-xs text-center">Loss</TableHead>
                    <TableHead className="font-heading font-bold uppercase text-xs text-center">Gain</TableHead>
                    <TableHead className="font-heading font-bold uppercase text-xs">By</TableHead>
                    <TableHead className="font-heading font-bold uppercase text-xs">Status</TableHead>
                    <TableHead className="font-heading font-bold uppercase text-xs text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stockTakeHistory.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-12 text-slate-500">
                        <History className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                        <p>No stock takes recorded yet</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    stockTakeHistory.map((st) => (
                      <TableRow key={st.id} className={st.status === 'in_progress' ? 'bg-yellow-50' : ''}>
                        <TableCell className="font-medium">
                          {formatDate(st.created_at)}
                          <p className="text-xs text-slate-500">{new Date(st.created_at).toLocaleTimeString()}</p>
                        </TableCell>
                        <TableCell className="capitalize">{st.store}</TableCell>
                        <TableCell>
                          {st.filter_type === 'all' ? (
                            <Badge variant="outline">All Products</Badge>
                          ) : st.filter_type === 'supplier' ? (
                            <Badge variant="outline" className="bg-orange-50">
                              <Truck className="w-3 h-3 mr-1" />
                              {st.filter_name || st.filter_value}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-purple-50">
                              <Tag className="w-3 h-3 mr-1" />
                              {st.filter_value}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline">{st.total_items_counted || 0}</Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          {st.total_loss_value > 0 ? (
                            <span className="text-red-600 font-medium">
                              -{st.total_loss_units || 0} (${st.total_loss_value?.toFixed(2) || '0.00'})
                            </span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {st.total_gain_value > 0 ? (
                            <span className="text-blue-600 font-medium">
                              +{st.total_gain_units || 0} (${st.total_gain_value?.toFixed(2) || '0.00'})
                            </span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">{st.created_by}</TableCell>
                        <TableCell>
                          <Badge className={st.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-yellow-100 text-yellow-700'}>
                            {st.status === 'completed' ? <Check className="w-3 h-3 mr-1" /> : <Play className="w-3 h-3 mr-1" />}
                            {st.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openStockTakeViewer(st)}
                            data-testid={`view-stocktake-${st.id}`}
                          >
                            View / Edit
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* START STOCK TAKE DIALOG */}
      <Dialog open={showStartDialog} onOpenChange={setShowStartDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5" />
              Start New Stock Take
            </DialogTitle>
            <DialogDescription>
              Choose what to count: all products, by supplier, or by brand
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Bold store warning */}
            <div className={`p-4 rounded-lg border-2 ${countStore === 'orewa' ? 'bg-emerald-50 border-emerald-400' : 'bg-blue-50 border-blue-400'}`}>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">You are about to count stock at</p>
              <p className={`text-3xl font-heading font-extrabold uppercase mt-1 ${countStore === 'orewa' ? 'text-emerald-700' : 'text-blue-700'}`} data-testid="store-warning-label">
                {countStore === 'orewa' ? '★ OREWA STORE ★' : '★ EASTRIDGE STORE ★'}
              </p>
              <p className="text-sm text-slate-700 mt-2">
                <strong>Double-check this is the store you're physically counting in.</strong> Picking the wrong store will adjust the wrong inventory.
              </p>
            </div>

            {/* Store Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Store</label>
              <div className="flex gap-2">
                <Button
                  variant={countStore === 'orewa' ? 'default' : 'outline'}
                  onClick={() => setCountStore('orewa')}
                  className={countStore === 'orewa' ? 'bg-emerald-600' : ''}
                >
                  {storeName('orewa')}
                </Button>
                <Button
                  variant={countStore === 'eastridge' ? 'default' : 'outline'}
                  onClick={() => setCountStore('eastridge')}
                  className={countStore === 'eastridge' ? 'bg-blue-600' : ''}
                >
                  {storeName('eastridge')}
                </Button>
              </div>
            </div>

            {/* Filter Type */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Count Type</label>
              <div className="grid grid-cols-3 gap-2">
                <Button
                  variant={filterType === 'all' ? 'default' : 'outline'}
                  onClick={() => { setFilterType('all'); setFilterValue(''); setFilterName(''); }}
                  className={filterType === 'all' ? 'bg-slate-700' : ''}
                >
                  <Package className="w-4 h-4 mr-2" /> All
                </Button>
                <Button
                  variant={filterType === 'supplier' ? 'default' : 'outline'}
                  onClick={() => setFilterType('supplier')}
                  className={filterType === 'supplier' ? 'bg-orange-500' : ''}
                >
                  <Truck className="w-4 h-4 mr-2" /> Supplier
                </Button>
                <Button
                  variant={filterType === 'brand' ? 'default' : 'outline'}
                  onClick={() => setFilterType('brand')}
                  className={filterType === 'brand' ? 'bg-purple-500' : ''}
                >
                  <Tag className="w-4 h-4 mr-2" /> Brand
                </Button>
              </div>
            </div>

            {/* Supplier Selection */}
            {filterType === 'supplier' && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Select Supplier</label>
                <Select 
                  value={filterValue} 
                  onValueChange={(val) => {
                    setFilterValue(val);
                    const supplier = suppliers.find(s => s.id === val);
                    setFilterName(supplier?.name || '');
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a supplier..." />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.sort((a, b) => a.name.localeCompare(b.name)).map(supplier => (
                      <SelectItem key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Brand Selection */}
            {filterType === 'brand' && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Select Brand</label>
                <Select value={filterValue} onValueChange={(val) => { setFilterValue(val); setFilterName(val); }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a brand..." />
                  </SelectTrigger>
                  <SelectContent>
                    {brands.map(brand => (
                      <SelectItem key={brand} value={brand}>
                        {brand}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Summary */}
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600">
                <strong>Summary:</strong> Count {filterType === 'all' ? 'all products' : 
                  filterType === 'supplier' ? `products from ${filterName || 'selected supplier'}` :
                  `${filterValue || 'selected brand'} products`} at {countStore.charAt(0).toUpperCase() + countStore.slice(1)} store.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStartDialog(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={handleStartStockTake}
              disabled={starting || (filterType !== 'all' && !filterValue)}
            >
              {starting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
              Start Counting
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* COMPLETE STOCK TAKE DIALOG */}
      <Dialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase flex items-center gap-2">
              <StopCircle className="w-5 h-5" />
              Complete Stock Take
            </DialogTitle>
            <DialogDescription>
              Review the results and choose whether to apply stock adjustments
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Summary */}
            <div className="grid grid-cols-2 gap-4">
              <Card className="bg-slate-50">
                <CardContent className="py-3">
                  <p className="text-sm text-slate-600">Items Counted</p>
                  <p className="text-2xl font-bold">{itemsCountedCount}</p>
                </CardContent>
              </Card>
              <Card className={totalVariance === 0 ? 'bg-emerald-50' : totalVariance < 0 ? 'bg-red-50' : 'bg-blue-50'}>
                <CardContent className="py-3">
                  <p className="text-sm text-slate-600">Net Variance</p>
                  <p className={`text-2xl font-bold ${totalVariance === 0 ? 'text-emerald-600' : totalVariance < 0 ? 'text-red-600' : 'text-blue-600'}`}>
                    {totalVariance > 0 ? '+' : ''}{totalVariance}
                  </p>
                </CardContent>
              </Card>
            </div>

            <Separator />

            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-red-50 rounded-lg">
                <p className="text-sm text-red-600 flex items-center gap-1 mb-1">
                  <ArrowDown className="w-4 h-4" /> Total Loss
                </p>
                <p className="text-xl font-bold text-red-700">{totalLossUnits} units</p>
                <p className="text-lg font-medium text-red-600">${totalLossValue.toFixed(2)}</p>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-600 flex items-center gap-1 mb-1">
                  <ArrowUp className="w-4 h-4" /> Total Gain
                </p>
                <p className="text-xl font-bold text-blue-700">{totalGainUnits} units</p>
                <p className="text-lg font-medium text-blue-600">${totalGainValue.toFixed(2)}</p>
              </div>
            </div>

            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> Completing will update stock levels to match your counted quantities. 
                This action cannot be undone.
              </p>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowCompleteDialog(false)}>
              Continue Counting
            </Button>
            <Button 
              variant="outline"
              onClick={() => handleCompleteStockTake(false)}
              disabled={completing}
            >
              Save Without Adjusting
            </Button>
            <Button 
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={() => handleCompleteStockTake(true)}
              disabled={completing}
            >
              {completing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              Complete & Adjust Stock
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View / Edit Stock Take Viewer */}
      <Dialog open={!!viewingStockTake} onOpenChange={(open) => { if (!open) { setViewingStockTake(null); setEditingRowProductId(null); } }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase">
              Stock Take Details
            </DialogTitle>
            <DialogDescription>
              {viewingStockTake && (
                <>
                  {viewingStockTake.store?.charAt(0).toUpperCase() + viewingStockTake.store?.slice(1)}
                  &nbsp;•&nbsp;{formatDate(viewingStockTake.created_at)}
                  &nbsp;•&nbsp;By {viewingStockTake.created_by || 'Unknown'}
                  &nbsp;•&nbsp;{viewingStockTake.status}
                  {viewingStockTake.adjustments_applied && <Badge className="ml-2 bg-emerald-100 text-emerald-700">Stock adjusted</Badge>}
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          {viewerLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : viewingStockTake && (
            <div className="flex flex-col flex-1 min-h-0 gap-3">
              {/* Summary cards */}
              <div className="grid grid-cols-4 gap-3 shrink-0">
                <Card className="p-3">
                  <p className="text-xs text-slate-500 uppercase">Items counted</p>
                  <p className="text-xl font-bold">{viewingStockTake.items?.length || 0}</p>
                </Card>
                <Card className="p-3">
                  <p className="text-xs text-slate-500 uppercase">Loss</p>
                  <p className="text-xl font-bold text-red-600">
                    {viewingStockTake.total_loss_units || 0} <span className="text-sm font-normal">(${(viewingStockTake.total_loss_value || 0).toFixed(2)})</span>
                  </p>
                </Card>
                <Card className="p-3">
                  <p className="text-xs text-slate-500 uppercase">Gain</p>
                  <p className="text-xl font-bold text-blue-600">
                    {viewingStockTake.total_gain_units || 0} <span className="text-sm font-normal">(${(viewingStockTake.total_gain_value || 0).toFixed(2)})</span>
                  </p>
                </Card>
                <Card className="p-3">
                  <p className="text-xs text-slate-500 uppercase">Net Variance</p>
                  <p className={`text-xl font-bold ${(viewingStockTake.total_variance || 0) < 0 ? 'text-red-600' : (viewingStockTake.total_variance || 0) > 0 ? 'text-blue-600' : 'text-slate-700'}`}>
                    {(viewingStockTake.total_variance || 0) > 0 ? '+' : ''}{viewingStockTake.total_variance || 0}
                  </p>
                </Card>
              </div>

              {/* Search */}
              <div className="relative shrink-0">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by product name or barcode..."
                  value={viewerSearch}
                  onChange={(e) => setViewerSearch(e.target.value)}
                  className="pl-9"
                  data-testid="viewer-search"
                />
              </div>

              {/* Items list */}
              <div className="flex-1 min-h-0 overflow-y-auto border rounded-sm">
                <Table>
                  <TableHeader className="sticky top-0 bg-slate-50 z-10">
                    <TableRow>
                      <TableHead className="font-heading text-xs uppercase">#</TableHead>
                      <TableHead className="font-heading text-xs uppercase">Product</TableHead>
                      <TableHead className="font-heading text-xs uppercase">Barcode</TableHead>
                      <TableHead className="font-heading text-xs uppercase text-center">System</TableHead>
                      <TableHead className="font-heading text-xs uppercase text-center">Counted</TableHead>
                      <TableHead className="font-heading text-xs uppercase text-center">Variance</TableHead>
                      <TableHead className="font-heading text-xs uppercase text-right">Edit</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(() => {
                      const term = (viewerSearch || '').toLowerCase().trim();
                      const words = term.split(/\s+/).filter(Boolean);
                      const items = (viewingStockTake.items || []).filter(it => {
                        if (!term) return true;
                        const hay = `${it.product_name || ''} ${it.barcode || ''} ${it.sku || ''}`.toLowerCase();
                        return words.every(w => hay.includes(w));
                      });
                      if (items.length === 0) {
                        return (
                          <TableRow>
                            <TableCell colSpan={7} className="text-center text-slate-500 py-8">
                              {(viewingStockTake.items || []).length === 0 ? 'No items recorded' : 'No items match your search'}
                            </TableCell>
                          </TableRow>
                        );
                      }
                      return items.map((it, idx) => {
                        const isEditing = editingRowProductId === it.product_id;
                        const variance = it.variance ?? 0;
                        return (
                          <TableRow key={it.product_id || idx}>
                            <TableCell className="text-xs text-slate-400">{idx + 1}</TableCell>
                            <TableCell className="font-medium">
                              {it.product_name}
                              {it.edited_at && (
                                <span className="ml-2 text-[10px] text-amber-600">edited {formatDateTime(it.edited_at)}</span>
                              )}
                            </TableCell>
                            <TableCell className="text-xs font-mono text-slate-500">{it.barcode || '-'}</TableCell>
                            <TableCell className="text-center">{it.system_stock}</TableCell>
                            <TableCell className="text-center">
                              {isEditing ? (
                                <Input
                                  type="number"
                                  min="0"
                                  value={editingRowValue}
                                  onChange={(e) => setEditingRowValue(e.target.value)}
                                  className="w-20 text-center mx-auto"
                                  autoFocus
                                  data-testid="editing-counted-input"
                                />
                              ) : (
                                <span className="font-semibold">{it.counted ?? 0}</span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              {variance === 0 ? (
                                <span className="text-slate-400">0</span>
                              ) : variance < 0 ? (
                                <span className="text-red-600 font-semibold">{variance}</span>
                              ) : (
                                <span className="text-blue-600 font-semibold">+{variance}</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              {isEditing ? (
                                <div className="flex gap-1 justify-end">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => { setEditingRowProductId(null); setEditingRowValue(''); }}
                                    disabled={savingRow}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    size="sm"
                                    className="bg-emerald-600 hover:bg-emerald-700"
                                    onClick={saveEditedCount}
                                    disabled={savingRow}
                                    data-testid="save-edited-count-btn"
                                  >
                                    {savingRow ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
                                  </Button>
                                </div>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setEditingRowProductId(it.product_id);
                                    setEditingRowValue(String(it.counted ?? 0));
                                  }}
                                >
                                  Edit
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      });
                    })()}
                  </TableBody>
                </Table>
              </div>

              {viewingStockTake.status === 'completed' && viewingStockTake.adjustments_applied && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 p-2 rounded shrink-0">
                  This stock take already adjusted live stock. Editing a count here will apply the difference to current product stock and log a "stock_take_edit" adjustment.
                </p>
              )}
            </div>
          )}

          <DialogFooter className="shrink-0">
            {viewingStockTake?.status === 'completed' && (
              <Button
                variant="outline"
                className="border-amber-400 text-amber-700 hover:bg-amber-50 mr-auto"
                onClick={async () => {
                  if (!viewingStockTake) return;
                  const wrong = viewingStockTake.store;
                  const right = wrong === 'orewa' ? 'eastridge' : 'orewa';
                  const wrongLabel = wrong.charAt(0).toUpperCase() + wrong.slice(1);
                  const rightLabel = right.charAt(0).toUpperCase() + right.slice(1);
                  const msg =
                    `⚠️  SWAP STOCK TAKE TO OTHER STORE\n\n` +
                    `This will:\n` +
                    `  1. UNDO every stock adjustment that was applied to ${wrongLabel}\n` +
                    `  2. APPLY the same counts to ${rightLabel} instead\n` +
                    `  3. Log both moves in adjustment history\n\n` +
                    `Items affected: ${viewingStockTake.items?.length || 0}\n\n` +
                    `Type SWAP to confirm:`;
                  const reply = window.prompt(msg);
                  if (!reply || reply.trim().toUpperCase() !== 'SWAP') {
                    toast.info('Swap cancelled');
                    return;
                  }
                  try {
                    const res = await axios.post(
                      `${API_URL}/stock-takes/${viewingStockTake.id}/swap-store`,
                      {},
                      { headers: getAuthHeader() }
                    );
                    toast.success(`Swapped: ${res.data.from_store} → ${res.data.to_store} • ${res.data.items_processed} items moved`);
                    // Refresh
                    const full = await axios.get(`${API_URL}/stock-takes/${viewingStockTake.id}`, { headers: getAuthHeader() });
                    setViewingStockTake(full.data);
                    const histRes = await axios.get(`${API_URL}/stock-takes`, { headers: getAuthHeader() });
                    setStockTakeHistory(histRes.data);
                  } catch (err) {
                    toast.error('Swap failed: ' + (err?.response?.data?.detail || err.message));
                  }
                }}
                data-testid="swap-store-btn"
              >
                Swap to {storeName(viewingStockTake?.store === 'orewa' ? 'eastridge' : 'orewa')}
              </Button>
            )}
            {viewingStockTake && (
              <Button
                variant="outline"
                className="border-red-400 text-red-700 hover:bg-red-50"
                data-testid="delete-stocktake-btn"
                onClick={async () => {
                  if (!viewingStockTake) return;
                  const isCompleted = viewingStockTake.status === 'completed';
                  const adjustmentsApplied = !!viewingStockTake.adjustments_applied;
                  let revert = false;
                  let msg;
                  if (isCompleted && adjustmentsApplied) {
                    msg =
                      `DELETE STOCK TAKE\n\n` +
                      `This stock take is COMPLETED and adjusted ${viewingStockTake.items?.length || 0} product stock levels.\n\n` +
                      `Choose what to do with the stock adjustments:\n\n` +
                      `  Type REVERT  → delete AND roll product stocks back to their pre-stocktake values\n` +
                      `  Type DELETE  → delete the record only (leaves current stock as-is)\n` +
                      `  Anything else → cancel`;
                  } else {
                    msg = `Delete this stock take?\n\nType DELETE to confirm:`;
                  }
                  const reply = (window.prompt(msg) || '').trim().toUpperCase();
                  if (reply === 'REVERT') {
                    revert = true;
                  } else if (reply !== 'DELETE') {
                    toast.info('Delete cancelled');
                    return;
                  }
                  try {
                    const url = `${API_URL}/stock-takes/${viewingStockTake.id}${revert ? '?revert_adjustments=true' : ''}`;
                    const res = await axios.delete(url, { headers: getAuthHeader() });
                    toast.success(res.data?.reverted ? 'Stock take deleted and stock reverted' : 'Stock take deleted');
                    setViewingStockTake(null);
                    setEditingRowProductId(null);
                    const histRes = await axios.get(`${API_URL}/stock-takes`, { headers: getAuthHeader() });
                    setStockTakeHistory(histRes.data);
                  } catch (err) {
                    toast.error('Delete failed: ' + (err?.response?.data?.detail || err.message));
                  }
                }}
              >
                <Trash2 className="w-4 h-4 mr-1" /> Delete
              </Button>
            )}
            <Button variant="outline" onClick={() => { setViewingStockTake(null); setEditingRowProductId(null); }}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


    </div>
  );
};

export default StockCount;
